import { useMutation } from "@apollo/client/react";
import { useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import useTypedContext from "hooks/useTypedContext";
import { getDrawerFormFix } from "utils/css";

import { CREATE_VCS_AGENT_POOL } from "../gql";

type FormValues = {
  name: string;
  description: string;
};

const VcsAgentPoolDrawer = createDrawer(() => {
  const drawer = useModal();
  const { onError } = useTypedContext(FlashContext);

  const [createVCSAgentPool, { loading }] = useMutation(CREATE_VCS_AGENT_POOL);

  const builderForm = useForm<FormValues>({
    defaultValues: {
      name: "",
      description: "",
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isDirty },
    register,
    formState: { errors },
  } = builderForm;

  const onCreateSubmit = useCallback(
    async (formData: FormValues) => {
      try {
        const { data } = await createVCSAgentPool({ variables: formData });
        if (data?.vcsAgentPoolCreate?.id) {
          drawer.resolve({ ...data.vcsAgentPoolCreate });
          drawer.hide();
        }
      } catch (error) {
        onError(error);
      }
    },
    [createVCSAgentPool, drawer, onError]
  );

  return (
    <DrawerForm isDirty={isDirty}>
      <FormProvider {...builderForm}>
        <form onSubmit={handleSubmit(onCreateSubmit)} {...getDrawerFormFix()}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title={"Create new VCS agent pool"} />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody gap="x-large">
            <FormField label="Name" error={errors?.name?.message} noMargin>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Enter VCS agent pool name"
                  error={!!errors?.name}
                  {...register("name", { required: "Name field is required." })}
                  {...ariaInputProps}
                />
              )}
            </FormField>
            <FormField label="Description" isOptional>
              {({ ariaInputProps }) => (
                <Textarea
                  placeholder="Enter VCS agent pool description here..."
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>
          </DrawerBody>
          <DrawerFooter>
            <DrawerFooterActions>
              <DrawerCancelButton />
              <Button variant="primary" type="submit" disabled={loading} loading={loading}>
                Create
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerForm>
  );
});

export const showVcsAgentPoolCreateDrawer = createDrawerTrigger(VcsAgentPoolDrawer);
